import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';

/** Tables Dexie exportées vers SQLite. */
export const DEXIE_MIGRATION_TABLES = [
  'tenants',
  'users',
  'salaries',
  'bulletins',
  'ecritures',
  'factures',
  'centres_analytiques',
  'audit_log',
] as const;

export type DexieMigrationTable = (typeof DEXIE_MIGRATION_TABLES)[number];

export interface DexieTableSnapshot {
  name: DexieMigrationTable;
  rows: Record<string, unknown>[];
}

/** Collecte un snapshot JSON des tables Dexie (pré-migration SQLite). */
export async function collectDexieSnapshot(db: NexxusDatabase): Promise<DexieTableSnapshot[]> {
  const out: DexieTableSnapshot[] = [];
  for (const name of DEXIE_MIGRATION_TABLES) {
    const table = (db as unknown as Record<string, { toArray: () => Promise<unknown[]> }>)[name];
    if (!table?.toArray) continue;
    const rows = (await table.toArray()) as Record<string, unknown>[];
    out.push({ name, rows });
  }
  return out;
}

export function countSnapshotRows(snapshots: DexieTableSnapshot[]): number {
  return snapshots.reduce((sum, t) => sum + t.rows.length, 0);
}

export interface DexieMigrationPlan {
  tables: Array<{ name: DexieMigrationTable; rowCount: number }>;
  totalRows: number;
  ready: boolean;
  blockers: string[];
}

const TENANT_SCOPED_TABLES = new Set<DexieMigrationTable>([
  'users',
  'salaries',
  'bulletins',
  'ecritures',
  'factures',
  'centres_analytiques',
  'audit_log',
]);

/** Valide l'intégrité d'un snapshot avant import SQLite. */
export function validateSnapshotForSqlite(snapshots: DexieTableSnapshot[]): string[] {
  const issues: string[] = [];
  const byName = new Map(snapshots.map((s) => [s.name, s]));

  for (const name of DEXIE_MIGRATION_TABLES) {
    if (!byName.has(name)) {
      issues.push(`table absente du snapshot: ${name}`);
    }
  }

  for (const snap of snapshots) {
    const seenIds = new Set<string>();
    for (const row of snap.rows) {
      if (!row || typeof row !== 'object') {
        issues.push(`${snap.name}: ligne non objet`);
        continue;
      }
      const id = row['id'];
      if (typeof id !== 'string' || !id.trim()) {
        issues.push(`${snap.name}: id manquant ou invalide`);
        continue;
      }
      if (seenIds.has(id)) {
        issues.push(`${snap.name}: id dupliqué ${id}`);
      }
      seenIds.add(id);

      if (TENANT_SCOPED_TABLES.has(snap.name)) {
        const tenantId = row['tenant_id'];
        if (typeof tenantId !== 'string' || !tenantId.trim()) {
          issues.push(`${snap.name}: tenant_id manquant pour ${id}`);
        }
      }
    }
  }

  return issues;
}

/** Plan de migration Dexie → SQLite (dry-run, sans écriture disque). */
export function planDexieToSqliteMigration(snapshots: DexieTableSnapshot[]): DexieMigrationPlan {
  const blockers = validateSnapshotForSqlite(snapshots);
  const tables = DEXIE_MIGRATION_TABLES.map((name) => {
    const snap = snapshots.find((s) => s.name === name);
    return { name, rowCount: snap?.rows.length ?? 0 };
  });
  return {
    tables,
    totalRows: countSnapshotRows(snapshots),
    ready: blockers.length === 0,
    blockers,
  };
}
